import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { languageName, type TranslatableType, type Translated } from '@tradeoutlet/shared';
import { api, errorMessage } from './api';
import { useMe, useWorkspaceId } from './hooks';

/**
 * Translation (#27): display-only. Requests are batched (max 50 per call) and results kept per item+language
 * for the session; the server caches by content hash so re-opening is free. Outgoing text is never translated.
 */
type Entry = { status: 'loading' | 'done' | 'error'; result?: Translated; error?: string; showOriginal: boolean; manual?: boolean };
const entries = new Map<string, Entry>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());
const key = (type: TranslatableType, id: string, target: string, version?: string | number | null) => `${type}:${id}:${target}:${version ?? ''}`;
let queue: { ws: string; target: string; type: TranslatableType; id: string; version?: string | number | null }[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function set(k: string, e: Entry) { entries.set(k, e); emit(); }

async function flush() {
  timer = null;
  const batch = queue; queue = [];
  const groups = new Map<string, typeof batch>();
  for (const q of batch) { const g = `${q.ws}|${q.target}`; groups.set(g, [...(groups.get(g) ?? []), q]); }
  for (const items of groups.values()) {
    for (let i = 0; i < items.length; i += 50) {
      const chunk = items.slice(i, i + 50);
      try {
        const out = await api.ai.translate(chunk[0].ws, chunk.map(c => ({ type: c.type, id: c.id })), chunk[0].target);
        for (const c of chunk) {
          const r = out.find(o => o.type === c.type && o.id === c.id);
          const k = key(c.type, c.id, c.target, c.version);
          set(k, r ? { status: 'done', result: r, showOriginal: entries.get(k)?.showOriginal ?? false, manual: entries.get(k)?.manual } : { status: 'error', error: 'Not available', showOriginal: true });
        }
      } catch (e) {
        for (const c of chunk) set(key(c.type, c.id, c.target, c.version), { status: 'error', error: errorMessage(e), showOriginal: true });
      }
    }
  }
}

export function requestTranslation(ws: string, type: TranslatableType, id: string, target: string, force = false, version?: string | number | null) {
  const k = key(type, id, target, version);
  const cur = entries.get(k);
  if (cur && cur.status !== 'error') { if (force) set(k, { ...cur, showOriginal: false, manual: true }); return; }
  if (cur?.status === 'error' && !force) return;
  set(k, { status: 'loading', showOriginal: false, manual: force });
  queue.push({ ws, type, id, target, version });
  timer ??= setTimeout(() => void flush(), 60);
}

export function useAiStatus() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: ['ai-status', ws], queryFn: () => api.ai.status(ws!), enabled: Boolean(ws), staleTime: 10 * 60_000 });
}

/** Language the reader wants (profile preference, else English) and whether auto-translate is on. */
export function useReaderLanguage() {
  const me = useMe();
  const p = me.data?.profile;
  return { target: p?.preferred_language ?? 'en', auto: Boolean(p?.auto_translate && p?.preferred_language), name: languageName(p?.preferred_language ?? 'en') };
}

/**
 * Translation state for one item. `auto` translates on mount when the reader turned on auto-translate;
 * skip it for your own posts. When the source is already in the reader's language nothing is shown.
 */
export function useTranslation(type: TranslatableType, id: string | null | undefined, opts: { auto?: boolean; version?: string | number | null; channelDefault?: boolean } = {}) {
  const ws = useWorkspaceId();
  const status = useAiStatus();
  const reader = useReaderLanguage();
  const available = Boolean(status.data?.translation.provider);
  const v = opts.version ?? null;
  const k = id ? key(type, id, reader.target, v) : '';
  const entry = useSyncExternalStore(cb => { listeners.add(cb); return () => { listeners.delete(cb); }; }, () => (k ? entries.get(k) : undefined));
  // A channel can translate by default (Channel settings → Translation) for readers who have a language set.
  const readerAuto = reader.auto || Boolean(opts.channelDefault && reader.target !== 'en');
  const autoOn = Boolean(opts.auto && readerAuto && available && ws && id);
  useEffect(() => { if (autoOn) requestTranslation(ws!, type, id!, reader.target, false, v); }, [autoOn, ws, type, id, reader.target, v]);
  const translated = entry?.status === 'done' && entry.result && !entry.result.same_language && !entry.showOriginal ? entry.result : null;
  return {
    available, entry, translated, target: reader.target, targetName: reader.name,
    loading: entry?.status === 'loading',
    sameLanguage: entry?.status === 'done' && Boolean(entry.result?.same_language),
    translate: () => { if (ws && id) requestTranslation(ws, type, id, reader.target, true, v); },
    showOriginal: () => { const e = k ? entries.get(k) : undefined; if (e) set(k, { ...e, showOriginal: true }); },
  };
}
